import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ReplyIcon from "@mui/icons-material/Reply";
import AddReactionOutlinedIcon from "@mui/icons-material/AddReactionOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  buildChatMentionOptions,
  chatMentionSegments,
  findActiveMentionAt,
  insertMentionAt,
  personKey,
} from "./videoChatMentions";
import {
  VIDEO_CHAT_ACCEPT,
  VIDEO_CHAT_MAX_FILE_BYTES,
  VIDEO_CHAT_QUICK_REACTIONS,
  groupVideoChatReactions,
  pickVideoChatMedia,
  videoChatHasMedia,
  videoChatMediaTypeFromFile,
  videoChatSnippet,
} from "./videoChatThread";
import { userIdFromAgentIdentity } from "./voOfficeLife";
import { formatTimePtBR } from "../../utils/dateTime";
import VideoChatMedia from "./VideoChatMedia";

function formatMsgTime(iso) {
  return formatTimePtBR(iso);
}

function focusComposer(inputRef) {
  const el = inputRef?.current;
  if (!el) return;
  try {
    if (typeof document !== "undefined" && document.activeElement === el) return;
    el.focus();
    const len = String(el.value || "").length;
    el.setSelectionRange(len, len);
  } catch {
    /* ignore */
  }
}

function ChatMessageBody({ body, localIdentity = "", localUserId = null, mine = false }) {
  const segments = useMemo(() => chatMentionSegments(body), [body]);
  const mineIdentity = String(localIdentity || "").trim();
  const mineUserId = Number(localUserId);
  if (segments.length === 1 && segments[0].type === "text") {
    return body;
  }
  return segments.map((seg, idx) => {
    if (seg.type !== "mention") return <React.Fragment key={idx}>{seg.text}</React.Fragment>;
    const forMe =
      Boolean(seg.mentionAll) ||
      (mineIdentity && String(seg.identity || "") === mineIdentity) ||
      (Number.isFinite(mineUserId) &&
        mineUserId > 0 &&
        Number(seg.userId) === mineUserId);
    return (
      <Box
        key={idx}
        component="span"
        sx={{
          fontWeight: 700,
          color: forMe ? "#fdd663" : mine ? "#d2e3fc" : "#aecbfa",
          bgcolor: forMe ? "rgba(253,214,99,0.16)" : "rgba(138,180,248,0.16)",
          px: 0.4,
          py: 0.05,
          borderRadius: 0.75,
        }}
      >
        {seg.text}
      </Box>
    );
  });
}

function ComposerFileChip({ file, kindLabel, onRemove, removeLabel }) {
  const [previewUrl, setPreviewUrl] = useState("");
  const isImage = videoChatMediaTypeFromFile(file) === "image";
  useEffect(() => {
    if (!isImage || !file) return undefined;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isImage]);
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        px: 1,
        py: 0.6,
        borderRadius: 1.5,
        bgcolor: "#3c4043",
        minWidth: 0,
      }}
    >
      {isImage && previewUrl ? (
        <Box
          component="img"
          src={previewUrl}
          alt=""
          sx={{ width: 36, height: 36, borderRadius: 1, objectFit: "cover", flexShrink: 0 }}
        />
      ) : (
        <AttachFileIcon sx={{ fontSize: 18, color: "#aecbfa", flexShrink: 0 }} />
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap sx={{ fontSize: "0.78rem", color: "#e8eaed" }}>
          {file.name}
        </Typography>
        <Typography sx={{ fontSize: "0.68rem", color: "#9aa0a6" }}>{kindLabel}</Typography>
      </Box>
      <IconButton size="small" onClick={onRemove} aria-label={removeLabel} sx={{ color: "#9aa0a6" }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

function QuotedBlock({ preview, youLabel, onClick, snippetLabels }) {
  if (!preview) return null;
  return (
    <Box
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      sx={{
        mb: 0.6,
        pl: 1,
        py: 0.4,
        pr: 0.5,
        minWidth: 0,
        maxWidth: "100%",
        overflow: "hidden",
        borderLeft: "3px solid #8ab4f8",
        borderRadius: 1,
        bgcolor: "rgba(255,255,255,0.06)",
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { bgcolor: "rgba(255,255,255,0.1)" } : undefined,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#aecbfa" }} noWrap>
        {preview.mine ? youLabel : preview.senderName || "Participante"}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.72rem",
          color: "#9aa0a6",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {videoChatSnippet(preview, { labels: snippetLabels })}
      </Typography>
    </Box>
  );
}

/**
 * Painel de chat in-call (estilo Meet) + botão da barra de controles.
 */
export function VideoInCallChatButton({ open, unreadCount = 0, title, onClick, disabled }) {
  const count = Number(unreadCount) || 0;
  const showBadge = count > 0 && !open;
  const label = showBadge ? `${title} (${count > 99 ? "99+" : count})` : title;
  return (
    <Box component="span" sx={{ position: "relative", display: "inline-flex" }}>
      <IconButton
        disabled={disabled}
        onClick={onClick}
        aria-label={label}
        title={label}
        sx={{
          width: 48,
          height: 48,
          bgcolor: open ? "rgba(138,180,248,0.24)" : "rgba(255,255,255,0.12)",
          color: open ? "#aecbfa" : "#fff",
          "&:hover": {
            bgcolor: open ? "rgba(138,180,248,0.32)" : "rgba(255,255,255,0.2)",
          },
          "&.Mui-disabled": {
            bgcolor: "rgba(255,255,255,0.12)",
            color: "#fff",
          },
        }}
      >
        <ChatIcon />
      </IconButton>
      {showBadge ? (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 2,
            right: 2,
            minWidth: 18,
            height: 18,
            px: 0.45,
            borderRadius: 999,
            bgcolor: "#ea4335",
            color: "#fff",
            fontSize: "0.65rem",
            fontWeight: 800,
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            boxShadow: "0 0 0 2px #3c4043",
          }}
        >
          {count > 99 ? "99+" : count}
        </Box>
      ) : null}
    </Box>
  );
}

export default function VideoInCallChat({
  open,
  onClose,
  messages = [],
  draft,
  onDraftChange,
  onSend,
  onReact,
  sending = false,
  loading = false,
  localIdentity = "",
  people = [],
  title = "Chat",
  placeholder = "Enviar mensagem",
  emptyHint = "Nenhuma mensagem ainda. Diga olá!",
  closeLabel = "Fechar chat",
  youLabel = "Você",
  mentionEveryoneLabel = "todos",
  mentionHint = "Pessoas no escritório",
  replyLabel = "Responder",
  reactLabel = "Reagir",
  replyingToLabel = "Respondendo a",
  cancelReplyLabel = "Cancelar resposta",
  attachLabel = "Anexar mídia",
  removeFileLabel = "Remover arquivo",
  openFileLabel = "Abrir arquivo",
  dropHint = "Solte para anexar",
  mediaTooLargeLabel = "Arquivo maior que 40 MB",
  snippetLabels,
  seedMention = null,
  onSeedMentionConsumed,
}) {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"));
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const [cursor, setCursor] = useState(0);
  const [mentionHighlightIndex, setMentionHighlightIndex] = useState(0);
  const [pickedMentions, setPickedMentions] = useState([]);
  const [mentionDismissed, setMentionDismissed] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [attachError, setAttachError] = useState("");
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const fileInputRef = useRef(null);
  const restoreCursorRef = useRef(null);

  const localUserId = userIdFromAgentIdentity(localIdentity);
  const activeMention = findActiveMentionAt(draft, cursor);
  const mentionOptions = useMemo(
    () =>
      activeMention
        ? buildChatMentionOptions(people, activeMention.query, {
            allLabel: mentionEveryoneLabel,
          })
        : [],
    [activeMention, mentionEveryoneLabel, people]
  );
  const mentionPickerOpen = Boolean(activeMention && mentionOptions.length && !mentionDismissed);

  useEffect(() => {
    setMentionHighlightIndex(0);
    setMentionDismissed(false);
  }, [activeMention?.query, mentionOptions.length]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [open, messages.length]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => focusComposer(inputRef), 120);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open || sending) return;
    const t = window.setTimeout(() => focusComposer(inputRef), 40);
    return () => window.clearTimeout(t);
  }, [open, sending]);

  useEffect(() => {
    if (!open || !seedMention) return;
    const next = insertMentionAt(draft, String(draft || "").length, seedMention);
    restoreCursorRef.current = next.cursor;
    setCursor(next.cursor);
    setPickedMentions((prev) => {
      const key = personKey(seedMention);
      return [...prev.filter((p) => personKey(p) !== key), seedMention];
    });
    onDraftChange?.(next.text);
    onSeedMentionConsumed?.();
    // Só consome o seed uma vez; draft entra pelo onDraftChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seedMention]);

  useEffect(() => {
    const pos = restoreCursorRef.current;
    if (pos == null) return;
    restoreCursorRef.current = null;
    const el = inputRef.current;
    if (!el) return;
    try {
      el.focus();
      el.setSelectionRange(pos, pos);
    } catch {
      /* ignore */
    }
  }, [draft]);

  const applyMention = (option) => {
    if (!option) return;
    const next = insertMentionAt(draft, cursor, option);
    restoreCursorRef.current = next.cursor;
    setCursor(next.cursor);
    setPickedMentions((prev) => {
      const key = option.type === "all" ? "all" : String(option.identity || option.id || option.name);
      const without = prev.filter((p) => {
        const pKey = p.type === "all" ? "all" : String(p.identity || p.id || p.name);
        return pKey !== key;
      });
      return [...without, option];
    });
    onDraftChange?.(next.text);
  };

  const startReply = (message) => {
    setReplyTo(message);
    setEmojiAnchor(null);
    window.setTimeout(() => focusComposer(inputRef), 40);
  };

  const addFiles = (incoming) => {
    const list = (Array.isArray(incoming) ? incoming : [incoming]).filter(Boolean);
    if (!list.length) return;
    const next = [];
    let tooLarge = false;
    for (const file of list) {
      if (Number(file.size) > VIDEO_CHAT_MAX_FILE_BYTES) {
        tooLarge = true;
        continue;
      }
      next.push(file);
    }
    if (tooLarge) setAttachError(mediaTooLargeLabel);
    else setAttachError("");
    if (next.length) setFiles((prev) => [...prev, ...next]);
  };

  const sendNow = () => {
    const hasText = Boolean(String(draft || "").trim());
    if (!hasText && !files.length) return;
    onSend?.({
      mentions: pickedMentions,
      files: files.slice(),
      replyToId: replyTo?.id || null,
      replyPreview: replyTo
        ? {
            id: replyTo.id,
            senderName: replyTo.senderName,
            body: replyTo.body,
            messageType: pickVideoChatMedia(replyTo).messageType,
            mediaUrl: pickVideoChatMedia(replyTo).mediaUrl || null,
            fileName: pickVideoChatMedia(replyTo).fileName || null,
          }
        : null,
    });
    setPickedMentions([]);
    setReplyTo(null);
    setFiles([]);
    setAttachError("");
    window.setTimeout(() => focusComposer(inputRef), 40);
  };

  const handleKeyDown = (e) => {
    if (mentionPickerOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionHighlightIndex((i) => (i + 1) % mentionOptions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionHighlightIndex((i) => (i - 1 + mentionOptions.length) % mentionOptions.length);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionDismissed(true);
        return;
      }
      if (e.key === "Tab" || (e.key === "Enter" && !e.shiftKey)) {
        e.preventDefault();
        applyMention(mentionOptions[mentionHighlightIndex]);
        return;
      }
    }
    if (e.key === "Escape" && replyTo) {
      e.preventDefault();
      setReplyTo(null);
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendNow();
    }
  };

  const jumpToMessage = (id) => {
    const mid = Number(id);
    if (!Number.isFinite(mid) || mid <= 0) return;
    const el = listRef.current?.querySelector?.(`[data-vo-chat-id="${mid}"]`);
    if (!el) return;
    try {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
    } catch {
      /* ignore */
    }
  };

  const panel = (
    <Box
      onDragEnter={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer?.types?.includes?.("Files")) setDragging(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        const dropped = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
        if (dropped.length) addFiles(dropped);
      }}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#202124",
        color: "#e8eaed",
        minWidth: 0,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          flexShrink: 0,
        }}
      >
        {dragging ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 4,
              bgcolor: "rgba(32,33,36,0.82)",
              display: "grid",
              placeItems: "center",
              border: "2px dashed #8ab4f8",
              pointerEvents: "none",
            }}
          >
            <Typography sx={{ color: "#aecbfa", fontWeight: 700 }}>{dropHint}</Typography>
          </Box>
        ) : null}
        <Typography sx={{ fontWeight: 600, fontSize: "1rem" }}>{title}</Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label={closeLabel}
          sx={{ color: "#9aa0a6" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box
        ref={listRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          px: 2,
          py: 1.5,
          minHeight: 0,
          minWidth: 0,
        }}
      >
        {loading && !messages.length ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
            <CircularProgress size={28} sx={{ color: "#aecbfa" }} />
          </Box>
        ) : !messages.length ? (
          <Typography sx={{ color: "#9aa0a6", fontSize: "0.875rem", textAlign: "center", py: 3 }}>
            {emptyHint}
          </Typography>
        ) : (
          <Stack spacing={1.5} sx={{ minWidth: 0, width: "100%" }}>
            {messages.map((m) => {
              const key = m.clientId || m.id || `${m.senderIdentity}-${m.createdAt}-${m.body}`;
              const mine =
                localIdentity &&
                String(m.senderIdentity || "") === String(localIdentity);
              const mentionedMe =
                !mine &&
                (Boolean(m.mentionAll) ||
                  chatMentionSegments(m.body).some(
                    (seg) =>
                      seg.type === "mention" &&
                      (seg.mentionAll ||
                        String(seg.identity || "") === String(localIdentity) ||
                        (localUserId && Number(seg.userId) === Number(localUserId)))
                  ));
              const grouped = groupVideoChatReactions(m.reactions);
              const canAct = Boolean(m.id) && !m.pending;
              const media = pickVideoChatMedia(m);
              const hasMedia = videoChatHasMedia(m);
              const caption = String(m.body || "").trim();
              const showCaption = Boolean(caption) && caption !== String(media.fileName || "").trim();
              return (
                <Box
                  key={key}
                  data-vo-chat-id={m.id || undefined}
                  sx={{
                    alignSelf: mine ? "flex-end" : "flex-start",
                    maxWidth: "100%",
                    minWidth: 0,
                    width: "100%",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ mb: 0.25, px: 0.25, minWidth: 0 }}
                  >
                    <Typography
                      noWrap
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        color: mine ? "#aecbfa" : "#fdd663",
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      {mine ? youLabel : m.senderName || "Participante"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.68rem", color: "#9aa0a6", flexShrink: 0 }}>
                      {formatMsgTime(m.createdAt)}
                    </Typography>
                    {canAct ? (
                      <Stack direction="row" spacing={0} sx={{ flexShrink: 0 }}>
                        <Tooltip title={replyLabel}>
                          <IconButton
                            size="small"
                            onClick={() => startReply(m)}
                            aria-label={replyLabel}
                            sx={{ color: "#c4c7c5", p: 0.35 }}
                          >
                            <ReplyIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={reactLabel}>
                          <IconButton
                            size="small"
                            onClick={(e) => setEmojiAnchor({ el: e.currentTarget, message: m })}
                            aria-label={reactLabel}
                            sx={{ color: "#c4c7c5", p: 0.35 }}
                          >
                            <AddReactionOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : null}
                  </Stack>
                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.85,
                      borderRadius: 2,
                      width: "fit-content",
                      maxWidth: "100%",
                      minWidth: 0,
                      ml: mine ? "auto" : 0,
                      overflow: "hidden",
                      bgcolor: mine
                        ? "rgba(138,180,248,0.16)"
                        : mentionedMe
                          ? "rgba(253,214,99,0.12)"
                          : "rgba(255,255,255,0.08)",
                      outline: mentionedMe ? "1px solid rgba(253,214,99,0.35)" : "none",
                      whiteSpace: "pre-wrap",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      fontSize: "0.9rem",
                      lineHeight: 1.45,
                      opacity: m.pending ? 0.7 : 1,
                    }}
                  >
                    <QuotedBlock
                      preview={
                        m.replyPreview
                          ? {
                              ...m.replyPreview,
                              mine:
                                localIdentity &&
                                messages.some(
                                  (row) =>
                                    Number(row.id) === Number(m.replyPreview.id) &&
                                    String(row.senderIdentity || "") === String(localIdentity)
                                ),
                            }
                          : null
                      }
                      youLabel={youLabel}
                      snippetLabels={snippetLabels}
                      onClick={m.replyPreview?.id ? () => jumpToMessage(m.replyPreview.id) : undefined}
                    />
                    {hasMedia ? (
                      <Box sx={{ mb: showCaption ? 0.75 : 0, minWidth: 0 }}>
                        <VideoChatMedia message={m} openFileLabel={openFileLabel} />
                      </Box>
                    ) : null}
                    {showCaption || !hasMedia ? (
                      <ChatMessageBody
                        body={m.body}
                        localIdentity={localIdentity}
                        localUserId={localUserId}
                        mine={mine}
                      />
                    ) : null}
                  </Box>
                  {grouped.length ? (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 0.45, justifyContent: mine ? "flex-end" : "flex-start" }}
                    >
                      {grouped.map((group) => {
                        const mineReact = group.users.some(
                          (u) => String(u.identity) === String(localIdentity)
                        );
                        const names = group.users.map((u) => u.userName || u.identity).join(", ");
                        return (
                          <Tooltip key={group.emoji} title={names || group.emoji}>
                            <Box
                              component="button"
                              type="button"
                              onClick={() => canAct && onReact?.(m, group.emoji)}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.35,
                                border: mineReact
                                  ? "1px solid rgba(138,180,248,0.7)"
                                  : "1px solid rgba(255,255,255,0.08)",
                                bgcolor: mineReact ? "rgba(138,180,248,0.16)" : "rgba(255,255,255,0.06)",
                                color: "#e8eaed",
                                borderRadius: 4,
                                px: 0.7,
                                py: 0.15,
                                cursor: canAct ? "pointer" : "default",
                                font: "inherit",
                              }}
                            >
                              <Box component="span" sx={{ fontSize: "0.95rem", lineHeight: 1 }}>
                                {group.emoji}
                              </Box>
                              {group.count > 1 ? (
                                <Typography sx={{ fontSize: "0.68rem", color: "#9aa0a6" }}>
                                  {group.count}
                                </Typography>
                              ) : null}
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Stack>
                  ) : null}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderTop: "1px solid rgba(255,255,255,0.08)",
          flexShrink: 0,
          position: "relative",
        }}
      >
        {replyTo ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
              px: 1,
              py: 0.7,
              borderRadius: 1.5,
              borderLeft: "3px solid #8ab4f8",
              bgcolor: "rgba(138,180,248,0.08)",
            }}
          >
            <ReplyIcon sx={{ fontSize: 16, color: "#8ab4f8" }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#aecbfa" }} noWrap>
                {replyingToLabel} {replyTo.senderName || "Participante"}
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: "#9aa0a6" }} noWrap>
                {videoChatSnippet(replyTo, { labels: snippetLabels })}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setReplyTo(null)}
              aria-label={cancelReplyLabel}
              sx={{ color: "#9aa0a6" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : null}
        {mentionPickerOpen ? (
          <Box
            role="listbox"
            aria-label={mentionHint}
            sx={{
              position: "absolute",
              left: 12,
              right: 56,
              bottom: "100%",
              mb: 0.75,
              maxHeight: 220,
              overflowY: "auto",
              bgcolor: "#2d3033",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 1.5,
              boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
              zIndex: 2,
            }}
          >
            <Typography
              sx={{
                px: 1.25,
                pt: 1,
                pb: 0.5,
                fontSize: "0.68rem",
                color: "#9aa0a6",
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}
            >
              {mentionHint}
            </Typography>
            {mentionOptions.map((opt, idx) => (
              <Box
                key={`${opt.type}-${opt.id}-${idx}`}
                role="option"
                aria-selected={idx === mentionHighlightIndex}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyMention(opt)}
                sx={{
                  px: 1.25,
                  py: 0.85,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "baseline",
                  gap: 0.75,
                  bgcolor: idx === mentionHighlightIndex ? "rgba(138,180,248,0.16)" : "transparent",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.88rem",
                    fontWeight: opt.type === "all" ? 800 : 600,
                    color: "#e8eaed",
                  }}
                  noWrap
                >
                  @{opt.name}
                </Typography>
                {opt.type === "all" ? (
                  <Typography sx={{ fontSize: "0.72rem", color: "#9aa0a6" }} noWrap>
                    {mentionHint}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Box>
        ) : null}
        {files.length ? (
          <Stack spacing={0.75} sx={{ mb: 1, minWidth: 0 }}>
            {files.map((file, index) => (
              <ComposerFileChip
                key={`${file.name}-${file.size}-${index}`}
                file={file}
                kindLabel={
                  videoChatMediaTypeFromFile(file) === "image"
                    ? snippetLabels?.image || "Imagem"
                    : videoChatMediaTypeFromFile(file) === "video"
                      ? snippetLabels?.video || "Vídeo"
                      : videoChatMediaTypeFromFile(file) === "audio"
                        ? snippetLabels?.audio || "Áudio"
                        : snippetLabels?.file || "Arquivo"
                }
                removeLabel={removeFileLabel}
                onRemove={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
              />
            ))}
          </Stack>
        ) : null}
        {attachError ? (
          <Typography sx={{ fontSize: "0.72rem", color: "#f28b82", mb: 0.75 }}>{attachError}</Typography>
        ) : null}
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          accept={VIDEO_CHAT_ACCEPT}
          onChange={(e) => {
            const selected = e.target.files ? Array.from(e.target.files) : [];
            if (selected.length) addFiles(selected);
            e.target.value = "";
          }}
        />
        <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ minWidth: 0 }}>
          <Tooltip title={attachLabel}>
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              aria-label={attachLabel}
              sx={{
                color: "#e8eaed",
                width: 40,
                height: 40,
                flexShrink: 0,
                bgcolor: "#3c4043",
                "&:hover": { bgcolor: "#4a4d51" },
              }}
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder={placeholder}
            value={draft}
            onChange={(e) => {
              setCursor(e.target.selectionStart ?? String(e.target.value || "").length);
              onDraftChange?.(e.target.value);
            }}
            onPaste={(e) => {
              const items = e.clipboardData?.items ? Array.from(e.clipboardData.items) : [];
              const pasted = items
                .filter((item) => item.kind === "file")
                .map((item) => item.getAsFile())
                .filter(Boolean);
              if (!pasted.length) return;
              e.preventDefault();
              addFiles(pasted);
            }}
            onSelect={(e) => {
              const el = e.target;
              if (el && typeof el.selectionStart === "number") setCursor(el.selectionStart);
            }}
            onKeyUp={(e) => {
              const el = e.target;
              if (el && typeof el.selectionStart === "number") setCursor(el.selectionStart);
            }}
            onClick={(e) => {
              const el = e.target;
              if (el && typeof el.selectionStart === "number") setCursor(el.selectionStart);
            }}
            onKeyDown={handleKeyDown}
            sx={{
              minWidth: 0,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#3c4043",
                color: "#e8eaed",
                borderRadius: 2,
                "& fieldset": { borderColor: "transparent" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,0.12)" },
                "&.Mui-focused fieldset": { borderColor: "#8ab4f8" },
              },
              "& .MuiInputBase-input": {
                overflowWrap: "anywhere",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#9aa0a6",
                opacity: 1,
              },
            }}
          />
          <IconButton
            onClick={sendNow}
            disabled={!String(draft || "").trim() && !files.length}
            sx={{
              bgcolor: "#8ab4f8",
              color: "#202124",
              width: 40,
              height: 40,
              flexShrink: 0,
              "&:hover": { bgcolor: "#aecbfa" },
              "&.Mui-disabled": {
                bgcolor: "rgba(138,180,248,0.3)",
                color: "rgba(32,33,36,0.5)",
              },
            }}
            aria-label={placeholder}
          >
            {sending && !String(draft || "").trim() && !files.length ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SendIcon fontSize="small" />
            )}
          </IconButton>
        </Stack>
      </Box>
      <Popover
        open={Boolean(emojiAnchor?.el)}
        anchorEl={emojiAnchor?.el || null}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              bgcolor: "#2d3033",
              color: "#e8eaed",
              borderRadius: 3,
              px: 0.5,
              py: 0.4,
            },
          },
        }}
      >
        <Stack direction="row" spacing={0.15}>
          {VIDEO_CHAT_QUICK_REACTIONS.map((emoji) => (
            <Box
              key={emoji}
              component="button"
              type="button"
              onClick={() => {
                if (emojiAnchor?.message) onReact?.(emojiAnchor.message, emoji);
                setEmojiAnchor(null);
                window.setTimeout(() => focusComposer(inputRef), 40);
              }}
              aria-label={emoji}
              sx={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: "1.35rem",
                lineHeight: 1,
                p: 0.7,
                borderRadius: 1.5,
                "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
              }}
            >
              {emoji}
            </Box>
          ))}
        </Stack>
      </Popover>
    </Box>
  );

  return (
    <Drawer
      anchor={isNarrow ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      keepMounted
      PaperProps={{
        sx: {
          width: isNarrow ? "100%" : 400,
          overflow: "hidden",
          height: isNarrow ? "min(70vh, calc(100dvh - 72px))" : "100%",
          maxHeight: isNarrow ? "min(70vh, calc(100dvh - 72px))" : "100%",
          bgcolor: "#202124",
          borderTopLeftRadius: isNarrow ? 16 : 0,
          borderTopRightRadius: isNarrow ? 16 : 0,
        },
      }}
      ModalProps={{
        keepMounted: true,
      }}
    >
      {panel}
    </Drawer>
  );
}
